import { Switch } from 'antd'
import { EditableSelect } from 'features/libra/components/EditableSelect'
import { useTicketStepContext } from 'features/libra/ticket/TicketStateContext'
import { FC, memo, useCallback } from 'react'
import styled from 'styled-components'

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const RawEditableBooleanSwitch: FC<{
  value: boolean | undefined
  onChange?: (value: boolean) => void
  trueLabel?: string
  falseLabel?: string
}> = ({ value, onChange, trueLabel = 'Yes', falseLabel = 'No' }) => {
  const { isStepComplete } = useTicketStepContext()

  const handleChange = useCallback(
    (checked: boolean) => {
      onChange?.(checked)
    },
    [onChange]
  )

  if (!onChange || isStepComplete) {
    return <>{value ? trueLabel : falseLabel}</>
  }

  return (
    <SwitchRow>
      <Switch checked={!!value} onChange={handleChange} />
      <span>{value ? trueLabel : falseLabel}</span>
    </SwitchRow>
  )
}

export const EditableBooleanSwitch = memo(RawEditableBooleanSwitch)

const BOOLEAN_OR_UNKNOWN_OPTIONS = [
  { value: 'true', label: 'True', color: 'green' },
  { value: 'false', label: 'False', color: 'red' },
  { value: 'unknown', label: 'Unknown', color: 'orange' },
] as const satisfies ReadonlyArray<{
  value: 'true' | 'false' | 'unknown'
  label: string
  color: string
}>

const RawBooleanOrUnknownSelect: FC<{
  value: 'true' | 'false' | 'unknown' | undefined
  onChange?: (value: 'true' | 'false' | 'unknown' | undefined) => void
}> = ({ value, onChange }) => {
  const displayValue = value ?? 'unknown'

  const handleChange = useCallback(
    (next: 'true' | 'false' | 'unknown') => {
      onChange?.(next === 'unknown' ? undefined : next)
    },
    [onChange]
  )

  return (
    <EditableSelect
      value={displayValue}
      onChange={handleChange}
      options={BOOLEAN_OR_UNKNOWN_OPTIONS}
    />
  )
}

export const BooleanOrUnknownSelect = memo(RawBooleanOrUnknownSelect)

const BOOLEAN_OR_UNKNOWN_OR_NO_HISTORY_OPTIONS = [
  { value: 'true', label: 'True', color: 'green' },
  { value: 'false', label: 'False', color: 'red' },
  { value: 'unknown', label: 'Unknown', color: 'orange' },
  { value: 'no_history', label: 'No History', color: 'geekblue' },
] as const satisfies ReadonlyArray<{
  value: 'true' | 'false' | 'unknown' | 'no_history'
  label: string
  color: string
}>

const RawBooleanOrUnknownOrNoHistorySelect: FC<{
  value: 'true' | 'false' | 'unknown' | 'no_history' | undefined
  onChange?: (value: 'true' | 'false' | 'unknown' | 'no_history') => void
}> = ({ value, onChange }) => {
  return (
    <EditableSelect
      value={value ?? 'unknown'}
      onChange={(v) => onChange?.(v)}
      options={BOOLEAN_OR_UNKNOWN_OR_NO_HISTORY_OPTIONS}
    />
  )
}

export const BooleanOrUnknownOrNoHistorySelect = memo(
  RawBooleanOrUnknownOrNoHistorySelect
)
